import { useMemo } from 'react';

import { useListAnswersQuery } from '@/@generated/codegen/API';

import extractBrandNameFromProductId from './extractBrandNameFromProductId';
import extractThemeIdFromProductId from './extractThemeIdFromProductId';

export type LorealBrandStats = {
  totalProductsSelected: number;
  productToAmtSelected: Map<string, number>;
};

export const createDefaultLorealBrandStats = (): LorealBrandStats => {
  return {
    totalProductsSelected: 0, // set your desired default value for totalProductsSelected
    productToAmtSelected: new Map<string, number>(), // initialize an empty Map
  };
};

export default function useStarProductAnswers(
  meetingID: string | null | undefined,
  currentPatientCaseId: string
) {
  const slideID = '37-68-5F';
  const answersQuery = useListAnswersQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {
        meetingId: {
          equals: meetingID!,
        },
        slideId: {
          equals: slideID,
        },
      },
    },
    skip: !meetingID || !currentPatientCaseId,
  });
  const answers = useMemo(() => {
    return answersQuery.data?.listAnswers.edges.map((edge) => edge.node) || [];
  }, [answersQuery.data]);
  const parsedStats = useMemo(() => {
    if (!meetingID) return null;
    if (answersQuery.loading || !answersQuery.data) return null;
    const answersPerBrandPerTheme = answers.reduce((acc, curr) => {
      curr.answerTexts!.forEach((productID, productIdx) => {
        if (productID === '') return;
        const themeId = extractThemeIdFromProductId(productID);
        const brandName = extractBrandNameFromProductId(productID);
        const key = `${themeId}_${brandName}`;
        if (!acc.has(key)) {
          acc.set(key, createDefaultLorealBrandStats());
        }
        const brandStats = acc.get(key)!;
        // NOTE: a star product has a voteWeight of 2 compared to a co star product
        const voteWeight = productIdx <= 2 ? 2 : 1;
        brandStats.totalProductsSelected += voteWeight;
        brandStats.productToAmtSelected.set(
          productID,
          (brandStats.productToAmtSelected.get(productID) || 0) + voteWeight
        );
        acc.set(key, brandStats);
      });

      return acc;
    }, new Map<string, LorealBrandStats>());
    return answersPerBrandPerTheme;
  }, [answers, meetingID]);
  return {
    isLoading: answersQuery.loading,
    data: parsedStats,
  };
}
